import React, { useState, useEffect } from "react";
import classes from "./editUser.module.scss";
import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import Divider from "@mui/material/Divider";
import {
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import toast from "react-hot-toast";
import { useUpdateUserMutation } from "../../../api/authApi";
import { useGetRolesQuery } from "../../../api/roleApi";
import { IRole } from "../../../interfaces/Itable";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { User } from "../../userControlComp/UserControlComp";

interface EditUserProps {
  user: NewUser;
  onClose: () => void;
}

interface NewUser {
  user_id?: number;
  emp_id?: string;
  username?: string;
  email?: string;
  password?: string;
  is_active?: boolean;
  role_id?: number;
  role_type?: string;
  roles?: {
    role_id: number;
    role_type: string;
    permissions?: {
      can_write: boolean;
      can_read: boolean;
      can_delete: boolean;
    };
  };
}

const EditUser: React.FC<EditUserProps> = ({ user, onClose }) => {
  const userInfo = useSelector(
    (state: RootState) => state.auth.user as User | null
  );
  const [updateUser, { isLoading }] = useUpdateUserMutation();
  const { data: roleOptions } = useGetRolesQuery();

  // ✅ Initialize editedUser properly, extracting nested role_id and role_type
  const [editedUser, setEditedUser] = useState<NewUser>({
    user_id: user.user_id,
    emp_id: user.emp_id,
    username: user.username,
    email: user.email,
    password: "",
    role_id: user.roles?.role_id || user.role_id,
    role_type: user.roles?.role_type || user.role_type,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [hasChanges, setHasChanges] = useState(false); // ✅ Track changes

  // ✅ Detect if any form values are changed
  useEffect(() => {
    const originalRoleId = user.roles?.role_id ?? user.role_id;
    const changed =
      editedUser.password?.trim() !== "" || // password entered
      editedUser.role_id !== originalRoleId; // role changed

    setHasChanges(changed);
  }, [editedUser, user]);

  // ✅ Password validation
  const validatePassword = (password?: string) => {
    if (!password) return true; // allow empty (means not changing password)
    const lengthOk = password.length >= 8;
    const upperOk = /[A-Z]/.test(password);
    const numberOk = /[0-9]/.test(password);
    const specialOk = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return lengthOk && upperOk && numberOk && specialOk;
  };

  // ✅ Form validation
  const isFormValid = () => {
    return (
      !!editedUser.user_id &&
      !!editedUser.role_id &&
      validatePassword(editedUser.password)
    );
  };

  const handleUpdateUser = async () => {
    if (!editedUser?.user_id || !editedUser?.role_id) {
      toast.error("Missing required fields");
      return;
    }

    if (editedUser.password && !validatePassword(editedUser.password)) {
      toast.error("Password does not meet required conditions");
      return;
    }

    try {
      const { user_id, role_id, password } = editedUser;
      const result = await updateUser({ id: user_id, role_id, password }).unwrap();

      toast[result.success ? "success" : "error"](
        result.message ||
        (result.success
          ? "User updated successfully!"
          : "Update failed!")
      );

      if (result.success) onClose();
    } catch (err: any) {
      console.error("Error updating user:", err);
      toast.error(err?.data?.message || "Failed to update user");
    }
  };

  return (
    <div className={classes.editUser}>
      <div className={classes.editUser__heading}>
        <h3>Edit User</h3>
        <Close sx={{ color: "red", cursor: "pointer" }} onClick={onClose} />
      </div>

      <Divider sx={{ width: "100%" }} />

      <div className={classes.editUser__formGrid}>
        <TextField label="EMP ID" value={editedUser.emp_id} size="small" disabled />
        <TextField label="User Name" value={editedUser.username} size="small" disabled />
        <TextField label="E-mail ID" value={editedUser.email} size="small" disabled />

        {/* Password */}
        <TextField
          label="Set New Password"
          type={showPassword ? "text" : "password"}
          value={editedUser.password || ""}
          size="small"
          onChange={(e) =>
            setEditedUser((prev) => ({ ...prev, password: e.target.value }))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* ✅ Role Autocomplete */}
        <Autocomplete<IRole>
          options={
            roleOptions
              ? roleOptions.filter((role) =>
                userInfo?.role_type === "Admin"
                  ? true
                  : role.role_type !== "Admin"
              )
              : []
          }
          getOptionLabel={(option) => option.role_type}
          isOptionEqualToValue={(option, value) =>
            option.role_id === value?.role_id
          }
          // ✅ Preselect previous role from API
          value={
            roleOptions?.find(
              (opt) =>
                opt.role_id ===
                (editedUser.role_id ??
                  editedUser.roles?.role_id ??
                  user.roles?.role_id)
            ) || null
          }
          onChange={(_, newValue) =>
            setEditedUser((prev) => ({
              ...prev,
              role_id: newValue?.role_id ?? prev.role_id,
              role_type: newValue?.role_type ?? prev.role_type,
              roles: newValue
                ? { role_id: newValue.role_id, role_type: newValue.role_type }
                : prev.roles,
            }))
          }
          renderInput={(params) => (
            <TextField {...params} label="Role" size="small" />
          )}
          slotProps={{
            paper: {
              sx: {
                fontFamily: "Poppins, sans-serif",
                "& .MuiAutocomplete-option": {
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                },
              },
            },
          }}
        />
      </div>

      {/* Password Rules */}
      {editedUser.password && (
        <div
          style={{ marginTop: "6px", fontSize: "12px", fontFamily: "Poppins" }}
        >
          <div
            style={{
              color: editedUser.password.length >= 8 ? "green" : "red",
            }}
          >
            • At least 8 characters
          </div>
          <div
            style={{
              color: /[A-Z]/.test(editedUser.password) ? "green" : "red",
            }}
          >
            • At least one uppercase letter
          </div>
          <div
            style={{
              color: /[0-9]/.test(editedUser.password) ? "green" : "red",
            }}
          >
            • At least one number
          </div>
          <div
            style={{
              color: /[!@#$%^&*(),.?":{}|<>]/.test(editedUser.password)
                ? "green"
                : "red",
            }}
          >
            • At least one special character
          </div>
        </div>
      )}

      <Divider sx={{ width: "100%" }} />

      <div className={classes.editUser__buttonBox}>
        <Button
          type="submit"
          variant="contained"
          sx={{ fontSize: "12px", fontFamily: "Poppins" }}
          onClick={handleUpdateUser}
          disabled={!isFormValid() || !hasChanges} // ✅ disable if unchanged
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default EditUser;
